// React imports
import React, { useState } from 'react';

// Utils
import _ from 'lodash';
import axios from 'axios';
import { Base64 } from 'js-base64';
import { v4 as uuidv4 } from 'uuid';

// MUI imports
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

// Local imports
import createMail from './gmail.createmail';
import GmailPoller from './gmail.poller';
import { messageToConversation, getMessageHtmlFromPayload, buildRfc822MultipartMixed, formatQuotedHeader } from './gmail.utils';

let _listener = null;
let _expiredListener = null;

function setConversationsListener(listener) {
    _listener = listener;
}

function setExpiredListener(listener) {
    _expiredListener = listener;
}

async function refreshConnection({ organizationId, accesstoken }) {

    const sessionId = window.localStorage.getItem('google-session-id') || uuidv4();
    window.localStorage.setItem('google-session-id', sessionId);

    try {
        const { data } = await axios.get(`/api/gmail?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
        console.log('[gmail] getMyInfo', data);
        if (data.status === 'expired') {
            console.log('[gmail] extending token');
            const { data: extendResponse } = await axios.get(`/api/gmail/extend?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);

            if (extendResponse.status === 'success') {
                console.log('[gmail] extending token - done');
                const { data: newData } = await axios.get(`/api/gmail?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
                console.log('[gmail] extending token - new data', newData);
                // setupPoller({ data: newData });
                return newData;
            } else {
                return { status: 'expired' };
            }
        } else {
            // setupPoller({ data });
            return data;
        }
    } catch (e) {
        console.log('[gmail] error with getMyInfo', e);
    }
}

async function forceRefreshConnection({ organizationId, accesstoken }) {
    const sessionId = window.localStorage.getItem('google-session-id') || uuidv4();
    window.localStorage.setItem('google-session-id', sessionId);

    try {
        console.log('[gmail] forcing token refresh');
        const { data: extendResponse } = await axios.get(`/api/gmail/extend?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);

        if (extendResponse.status === 'success') {
            console.log('[gmail] forced token refresh - done');
            const { data: newData } = await axios.get(`/api/gmail?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
            console.log('[gmail] forced token refresh - new data', newData);
            return newData;
        } else {
            return { status: 'expired' };
        }
    } catch (e) {
        console.log('[gmail] error with force refresh', e);
        throw e;
    }
}

async function disconnect({ organizationId, accesstoken }) {
    const sessionId = window.localStorage.getItem('google-session-id') || uuidv4();
    window.localStorage.setItem('google-session-id', sessionId);

    return await axios.delete(`/api/gmail/?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
}

function LoginButton(props) {
    const { organizationId, accesstoken, onDone, sx } = props;
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState(null);

    const sessionId = window.localStorage.getItem('google-session-id') || uuidv4();
    window.localStorage.setItem('google-session-id', sessionId);

    function onConnect() {
        setError(null);
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
        }, 500);
        window.gmailCodeClient.callback = async (resp) => {
            if (resp.error !== undefined) {
                throw (resp);
            }

            const { code } = resp;

            await axios.get('/api/gmail/login?googleCode=' + encodeURIComponent(code) + '&organizationId=' + organizationId + '&accesstoken=' + accesstoken + '&sessionId=' + sessionId);

            onDone();
        };

        window.gmailCodeClient.requestCode();
    }

    return (
        <>
            <Button
                variant='contained'
                color='monada-button'
                onClick={onConnect}
                disabled={loading}
                sx={ sx || { minWidth: 300 } }
            >
                {loading ? <CircularProgress size={24} /> : 'Connect to Gmail'}
            </Button>
            {error && (
                <Alert severity='error' sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}
        </>
    );
}

async function getMessagesForConversation({ conversationId, internetMessageId, accesstoken }) {
    window.gapi.auth.setToken({ access_token: accesstoken });

    if (internetMessageId) {
        const mid = String(decodeURIComponent(internetMessageId)).trim().replace(/^<|>$/g, ''); // drop < >

        // 1) Find the thread that contains this Message-ID
        const listRes = await window.gapi.client.gmail.users.threads.list({
            userId: 'me',
            q: `rfc822msgid:${mid}`,
            includeSpamTrash: true,
            maxResults: 1,
            fields: 'threads/id,resultSizeEstimate'
        });

        const thread = listRes.result.threads?.[0];
        if (!thread) return null; 
        conversationId = thread.id;
    } 

    const response = await window.gapi.client.gmail.users.threads.get({
        userId: 'me',
        id: conversationId,
        format: 'full',
        fields: 'id,messages(id,threadId,labelIds,internalDate,payload/headers,payload/parts,payload/mimeType,payload/body)'
    });

    if (response.result.messages.length === 0) {
        return null;
    }

    return _(response.result.messages).map(m => messageToConversation(m))
        .sortBy(m => -1 * new Date(m.receivedDateTime).getTime()).value();
}

async function markAsRead({ messageIds, accesstoken }) {
    window.gapi.auth.setToken({ access_token: accesstoken });
    await window.gapi.client.gmail.users.messages.batchModify({
        userId: 'me',
        ids: messageIds,
        removeLabelIds: ['UNREAD'],
    });
}

async function markAsUnread({ messageIds, accesstoken }) {
    window.gapi.auth.setToken({ access_token: accesstoken });
    await window.gapi.client.gmail.users.messages.batchModify({
        userId: 'me',
        ids: messageIds,
        addLabelIds: ['UNREAD'],
    });
}

async function createDraft({ accesstoken, toRecipients, ccRecipients, subject, body, attachment, monadaAccesstoken, monadaOrganizationId }) {
    const _body = createMail({
        headers: {
            To: toRecipients.map(r => r.address).join(','),
            ...(!_.isEmpty(ccRecipients) ? { Cc: ccRecipients?.map(r => r.address).join(',') } : {}),
            Subject: subject
        },
        textHtml: body,
        attachments: attachment ? [attachment] : []
    })

    const { data } = await axios.post('/api/gmail/forward?accesstoken=' + monadaAccesstoken + '&organizationId=' + monadaOrganizationId, {
        url: 'drafts',
        body: _body,
        headers: {
            Authorization: `Bearer ${accesstoken}`,
            'Content-Type': 'multipart/related; boundary="foo_bar_baz"'
        },
    });
    return data;
}

async function sendEmail({ accesstoken, toRecipients, ccRecipients, bccRecipients, subject, body, attachments = [], monadaAccesstoken, monadaOrganizationId }) {
    console.log('[gmail] sending email', toRecipients, ccRecipients, subject, body, attachments, monadaAccesstoken, monadaOrganizationId);
    const _body = createMail({
        headers: {
            To: toRecipients.map(r => r.address).join(','),
            ...(!_.isEmpty(ccRecipients) ? { Cc: ccRecipients?.map(r => r.address).join(',') } : {}),
            ...(!_.isEmpty(bccRecipients) ? { Bcc: bccRecipients?.map(r => r.address).join(',') } : {}),
            Subject: subject
        },
        textHtml: body,
        attachments: attachments
    })

    const { data } = await axios.post('/api/gmail/forward?accesstoken=' + monadaAccesstoken + '&organizationId=' + monadaOrganizationId, {
        url: 'messages/send',
        body: _body,
        headers: {
            Authorization: `Bearer ${accesstoken}`,
            'Content-Type': 'multipart/related; boundary="foo_bar_baz"'
        },
    });

    const messages = await getMessagesForConversation({ conversationId: data.threadId, accesstoken });
    const lastMessage = _.last(messages);
    if (!lastMessage) return null;
    return messageToConversation({ ...(lastMessage.origin || {}), ...lastMessage });
}

async function sendReply({
    conversationId,
    accesstoken,               // Gmail OAuth token (sender)
    toRecipients,
    ccRecipients,
    body,                      // your new HTML reply (without quote)
    attachments,               // [{ name, type, data }]
    monadaAccesstoken,
    monadaOrganizationId,
}) {
    // 1) fetch thread + pick latest message
    const messages = await getMessagesForConversation({ conversationId, accesstoken });
    const lastMsg = messages
        .map(m => m.origin)
        .sort((a, b) => Number(a.internalDate || 0) - Number(b.internalDate || 0))
        .at(-1);

    const headers = (lastMsg?.payload?.headers || []);
    const getHeader = (name) =>
        headers.find(h => String(h.name || '').toLowerCase() === name.toLowerCase())?.value || '';

    const subjectRaw = getHeader('Subject') || '';
    const subject = subjectRaw.trim().toLowerCase().startsWith('re:') ? subjectRaw : `Re: ${subjectRaw}`;
    const lastMessageId = getHeader('Message-Id');           // e.g. '<abc@domain>'
    const prevReferences = getHeader('References') || '';
    const references = [prevReferences, lastMessageId].filter(Boolean).join(' ').trim();

    // 2) extract previous message HTML
    const prevHtml = getMessageHtmlFromPayload(lastMsg?.payload) || '';
    const fromHeader = getHeader('From');
    const dateHeader = getHeader('Date');

    // 3) compose quoted block
    const quotedHeader = formatQuotedHeader({ from: fromHeader, date: dateHeader });
    const quotedHtml =
        `<div><br></div>` + // spacer after your reply
        `<div>${quotedHeader}</div>` +
        `<blockquote style='margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex;'>${prevHtml}</blockquote>`;

    // 4) final HTML = your reply + quoted previous content
    const finalHtml = (body || '') + quotedHtml;

    const to = (toRecipients || []).map(r => _.isString(r) ? r : r.address).filter(Boolean).join(',');
    const cc = (ccRecipients || []).map(r => _.isString(r) ? r : r.address).filter(Boolean).join(',');
    if (!to && !cc) throw new Error('No recipients provided (To/Cc).');

    // If you send via alias, set this (must be configured in Gmail 'Send mail as')
    const from = undefined;

    // 5) build RFC-822 and send via your upload proxy
    const rfc822 = buildRfc822MultipartMixed({
        from,
        to,
        cc,
        subject,
        inReplyTo: lastMessageId || undefined,
        references: references || undefined,
        html: finalHtml,
        attachments: attachments || [],
    });

    const boundary = 'foo_bar_baz_' + Math.random().toString(36).slice(2);
    const multipartBody =
        `--${boundary}\r\n` +
        `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
        JSON.stringify({ threadId: conversationId }) + `\r\n` +
        `--${boundary}\r\n` +
        `Content-Type: message/rfc822\r\n\r\n` +
        rfc822 + `\r\n` +
        `--${boundary}--`;

    const { data } = await axios.post(
        `/api/gmail/forward?accesstoken=${monadaAccesstoken}&organizationId=${monadaOrganizationId}`,
        {
            url: 'messages/send?uploadType=multipart',
            body: multipartBody,
            headers: {
                Authorization: `Bearer ${accesstoken}`,
                'Content-Type': `multipart/related; boundary='${boundary}'`,
            },
        }
    );

    return data;
}

async function createDraftReply({ conversationId, accesstoken, toRecipients, ccRecipients, body, attachment, monadaAccesstoken, monadaOrganizationId }) {
    const messages = await getMessagesForConversation({ conversationId, accesstoken });

    const inReplyTo = messages[0].origin.payload.headers.find(h => h.name === 'In-Reply-To')?.value;
    const references = messages[0].origin.payload.headers.find(h => h.name === 'References')?.value;
    const subject = messages[0].origin.payload.headers.find(h => h.name === 'Subject')?.value;

    const _body = createMail({
        url: 'messages/send',
        threadId: conversationId,
        headers: {
            To: toRecipients.map(r => r.address).join(','),
            ...(!_.isEmpty(ccRecipients) ? { Cc: ccRecipients?.map(r => r.address).join(',') } : {}),
            Subject: subject,
            'In-Reply-To': inReplyTo,
            References: references,
        },
        textHtml: body,
        attachments: attachment ? [attachment] : []
    })

    const { data } = await axios.post('/api/gmail/forward?accesstoken=' + monadaAccesstoken + '&organizationId=' + monadaOrganizationId, {
        url: 'drafts',
        body: _body,
        headers: {
            Authorization: `Bearer ${accesstoken}`,
            'Content-Type': 'multipart/related; boundary="foo_bar_baz"'
        },
    });

    return data;
}

async function getAttachmentUrl({ messageId, attachment, accesstoken, monadaAccesstoken, monadaOrganizationId }) {
    const { data } = await axios.post(`/api/gmail/download-attachment?accesstoken=${monadaAccesstoken}&organizationId=${monadaOrganizationId}`,
        {
            messageId,
            attachmentId: attachment.id,
            gmailAccesstoken: accesstoken,
            contentType: attachment.contentType,
            name: attachment.name
        });
    return data.url;
}

async function archive({ conversationId, accesstoken, monadaAccesstoken, monadaOrganizationId }) {
    console.log('[gmail] archive', conversationId, accesstoken, monadaAccesstoken, monadaOrganizationId);
    window.gapi.auth.setToken({ access_token: accesstoken });

    const response = await window.gapi.client.gmail.users.threads.modify({
        userId: 'me',
         id: conversationId,
        removeLabelIds: ['INBOX'],
    });

    return response.result;
}

async function trash({ conversationId, accesstoken, monadaAccesstoken, monadaOrganizationId }) {
    console.log('[gmail] trash', conversationId, accesstoken, monadaAccesstoken, monadaOrganizationId);
    window.gapi.auth.setToken({ access_token: accesstoken });

    const response = await window.gapi.client.gmail.users.threads.trash({
        userId: 'me',
        id: conversationId,
    });

    return response.result;
}

async function getLabels({ accesstoken }) {
    try {
        window.gapi.auth.setToken({ access_token: accesstoken });
        const response = await window.gapi.client.gmail.users.labels.list({
            userId: 'me',
        });

        const labels = response.result.labels;

        return labels.filter(l => l.type === 'user');
    } catch (e) {
        console.log('[gmail] error with getLabels', e);
        _expiredListener();
        throw e;
    }
}

function getDefaultFolderId() {
    return 'INBOX';
}

async function getFolders({ accesstoken }) {
    try {
        window.gapi.auth.setToken({ access_token: accesstoken });
        const response = await window.gapi.client.gmail.users.labels.list({
            userId: 'me',
        });

        const labels = response.result.labels;

        return labels.filter(l => l.type === 'system' && l.labelListVisibility !== 'labelHide');
    } catch (e) {
        console.log('[gmail] error with getFolders', e);
        // Check if this is an expired token error
        const errorMessage = e?.message || e?.error?.message || JSON.stringify(e);
        const isExpiredError = errorMessage?.includes('Lifetime validation failed') || 
                              errorMessage?.includes('expired') ||
                              errorMessage?.includes('InvalidAuthenticationToken') ||
                              e?.statusCode === 401 ||
                              e?.code === 'InvalidAuthenticationToken';
        
        if (isExpiredError && _expiredListener) {
            console.log('[gmail] Token expired in getFolders, calling expired listener');
            _expiredListener();
            // Don't throw the error - it's been handled by the listener
            // Return empty array to indicate no folders could be fetched due to expired token
            return [];
        }
        throw e;
    }
}

async function getConversations({ accesstoken, folder, search, nextPageToken }) {
    try {
        window.gapi.auth.setToken({ access_token: accesstoken });

        console.log('[gmail] getConversations', accesstoken, folder, search, nextPageToken);

        const folderId = {
            inbox: 'INBOX',
            sent: 'SENT',
            draft: 'DRAFT',
            flagged: 'STARRED',
        }[folder] || folder;

        const { result } = await window.gapi.client.gmail.users.threads.list({
            userId: 'me',
            labelIds: [folderId],     // optional
            maxResults: 50,          // conversations, not messages
            ...(search ? { q: search } : {}), // optional Gmail search query
            ...(nextPageToken ? { pageToken: nextPageToken } : {}), // optional next page token
            fields: 'threads/id,threads/snippet,nextPageToken' // partial response
        });

        console.log('[gmail] getConversations', result);

        const conversations = await Promise.all((result.threads || []).map(async t => {

            let res = null;
            let attempts = 0;
            do {
                try {
                    attempts++;
                    res = await window.gapi.client.gmail.users.threads.get({
                        userId: 'me',
                        id: t.id,
                        format: 'metadata',
                        metadataHeaders: ['Subject', 'From', 'To', 'Date', 'Message-ID'],
                        fields: 'id,messages(id,internalDate,labelIds,snippet,payload/headers,payload/parts)'
                    });
                } catch (err) {
                    const code = err?.status || err?.result?.error?.code;
                    const reason = err?.result?.error?.errors?.[0]?.reason || '';
                    if (attempts < 3 && (code === 429 || reason === 'rateLimitExceeded' || reason === 'userRateLimitExceeded')) {
                        await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 1000) + 1000));
                    } else {
                        throw err;
                    }
                }
            } while (res === null);

            const threadSnippet = t.snippet;
            const conversation = (res.result.messages || []).map((m, index) => {
                // Use thread snippet for first message if individual message snippet is not available
                const messageWithSnippet = index === 0 && !m.snippet && threadSnippet 
                    ? { ...m, snippet: threadSnippet }
                    : m;
                return messageToConversation({ ...messageWithSnippet, threadId: t.id });
            });
            return conversation;
        }));

        // Sort conversations by most recent message (latest message in each conversation)
        conversations.sort((a, b) => {
            // Find the latest message in each conversation
            const aLatest = a.reduce((latest, msg) => {
                const msgDate = new Date(msg.receivedDateTime);
                return msgDate > latest ? msgDate : latest;
            }, new Date(0));
            const bLatest = b.reduce((latest, msg) => {
                const msgDate = new Date(msg.receivedDateTime);
                return msgDate > latest ? msgDate : latest;
            }, new Date(0));
            return bLatest - aLatest; // Sort descending (newest first)
        });

        return { conversations, nextPageToken: result.nextPageToken };
    } catch (e) {
        console.log('[gmail] error with getConversations', e);
        // Check if this is an expired token error
        const errorMessage = e?.message || e?.error?.message || JSON.stringify(e);
        const isExpiredError = errorMessage?.includes('Lifetime validation failed') || 
                              errorMessage?.includes('expired') ||
                              errorMessage?.includes('InvalidAuthenticationToken') ||
                              e?.statusCode === 401 ||
                              e?.code === 'InvalidAuthenticationToken';
        
        if (isExpiredError && _expiredListener) {
            console.log('[gmail] Token expired, calling expired listener');
            _expiredListener();
            // Don't throw the error - it's been handled by the listener
            // Return empty conversations to indicate no conversations could be fetched due to expired token
            return { conversations: [], nextPageToken: null };
        }
        throw e;
    }
}

async function setConversationFlag({ conversationId, accesstoken, flagged }) {
    window.gapi.auth.setToken({ access_token: accesstoken });
    await window.gapi.client.gmail.users.threads.modify({
        userId: 'me',
        id: conversationId,
        addLabelIds: flagged ? ['STARRED'] : [],
        removeLabelIds: flagged ? [] : ['STARRED'],
    });
}

export default { setConversationsListener, setExpiredListener, LoginButton, refreshConnection, forceRefreshConnection, disconnect,
    getConversations, getMessagesForConversation, createDraft, sendEmail, sendReply, createDraftReply, getAttachmentUrl, 
    archive, trash, markAsRead, markAsUnread, getLabels, setConversationFlag, getFolders, getDefaultFolderId };
